import { css } from '@lastbrand/web-ui-styled-system/css'

import { VideoDS, VIDEO_LOADING_TYPE, VIDEO_PRELOAD_TYPE } from '@src/components/blocks/video-ds'
import { Image } from '@src/quince-components/src'

import type { FeatureGridDSProps } from './feature-grid-ds.types'

// Beside the grid, sharing an even 50/50 split with it on desktop; a 360×300 banner on top for
// mobile. On desktop the `1 / 1` ratio sets this cell's HYPOTHETICAL height only — with
// `alignItems: stretch` on the row, the row resolves to max(text-column height, this square), so the
// media keeps the designed square when the text is short but stretches taller than 1:1 when the text
// is tall (STRFRNT-15853 — the text cell carries no ratio and no overflow clip, so copy is never
// cut). `position: relative` anchors the absolutely-filled media so the image/video can't feed its
// own intrinsic height back into the row. `flex: 1 1 50%` keeps the even split when both cells render
// while letting whichever cell renders alone grow to fill the row; `minWidth: 0` matches the Figma
// frame's `min-w-px` so content clips instead of pushing past 50%.
const mediaWrapClass = css({
  width: { base: 'full' },
  flex: { lg: '1 1 50%' },
  minWidth: { lg: 0 },
  aspectRatio: { base: '360 / 300', lg: '1 / 1' },
  overflow: 'hidden',
  position: { lg: 'relative' },
})
// On desktop the media is absolutely positioned to fill the stretched wrapper (so it fills the
// text-driven height with `cover`); on mobile it fills the 360×300 aspect box in normal flow.
const mediaFillClass = css({
  width: 'full',
  height: 'full',
  objectFit: 'cover',
  position: { lg: 'absolute' },
  inset: { lg: '0' },
})

type FeatureGridMediaProps = Pick<FeatureGridDSProps, 'imageProps' | 'videoProps'>

/** The hero media. Legacy `FeatureSectionTwo` preferred a looping muted video over the image. */
export function FeatureGridMedia({ imageProps, videoProps }: FeatureGridMediaProps) {
  if (videoProps?.url) {
    return (
      <VideoDS
        url={videoProps.url}
        contentType={videoProps.contentType}
        title={videoProps.title}
        muted
        autoplay
        loop
        loadingType={VIDEO_LOADING_TYPE.Lazy}
        preload={VIDEO_PRELOAD_TYPE.Auto}
        wrapperClassName={mediaWrapClass}
        className={mediaFillClass}
      />
    )
  }

  // No video and no image url: render nothing rather than an empty `<img src="">` (bad request + broken slot).
  if (!imageProps.url) return null

  return (
    <div className={mediaWrapClass}>
      {/* eslint-disable-next-line i18next/no-literal-string -- loading attr value */}
      <Image {...imageProps} alt={imageProps.alt || imageProps.title} className={mediaFillClass} loading="lazy" />
    </div>
  )
}
